// components/schema/graph-utils.js

export function parseGraphFromCode(text) {
  const nodes = new Map()
  const links = []

  const extractProps = (block) => {
    const props = []
    const propRegex = /(\w+)\s+(\w+)(\s+OPTIONAL)?/g
    let m
    while ((m = propRegex.exec(block)) !== null) {
      const [, name, type, opt] = m
      props.push({
        name,
        type,
        optional: Boolean(opt?.trim()),
      })
    }
    // deduplicate by name+type
    return [...new Map(props.map((p) => [`${p.name}:${p.type}`, p])).values()]
  }

  // NODE: allow multiple labels after the colon: Label1, Label2, Label3
  const nodeDecl =
    /CREATE\s+NODE\s+TYPE\s*\(\s*([A-Za-z0-9_-]+)\s*:\s*([A-Za-z0-9_-]+(?:\s*,\s*[A-Za-z0-9_-]+)*)\s*\{([^}]*)\}\s*\)/gim

  let nm
  while ((nm = nodeDecl.exec(text)) !== null) {
    const typeName = nm[1]
    const labelsRaw = nm[2] || ""
    const propsBlock = nm[3] || ""

    const labels = labelsRaw
      .split(",")
      .map((l) => l.trim())
      .filter(Boolean)

    const props = extractProps(propsBlock)

    if (!nodes.has(typeName)) {
      nodes.set(typeName, {
        id: typeName,
        group: typeName,
        labels,
        props,
      })
    } else {
      // merge unique props if node already exists
      const existing = nodes.get(typeName)
      const mergedProps = [...existing.props, ...props]
      existing.props = [
        ...new Map(mergedProps.map((p) => [`${p.name}:${p.type}`, p])).values(),
      ]

      // merge labels too
      const mergedLabels = [...(existing.labels || []), ...labels]
      existing.labels = [
        ...new Set(
          mergedLabels.map((l) => l.trim()).filter(Boolean)
        ),
      ]
    }
  }

  // EDGE: also allow multiple labels after colon: Label1, Label2, ...
  const edgeDecl =
    /CREATE\s+EDGE\s+TYPE\s*\(\s*([A-Za-z0-9_-]+)\s*\)\s*-\s*\[\s*([A-Za-z0-9_-]+)\s*:\s*([A-Za-z0-9_-]+(?:\s*,\s*[A-Za-z0-9_-]+)*)\s*\{([^}]*)\}\s*\]\s*->\s*\(\s*([A-Za-z0-9_-]+)\s*\)/gim

  let em
  while ((em = edgeDecl.exec(text)) !== null) {
    const src = em[1]
    const edgeType = em[2]
    const labelsRaw = em[3] || ""
    const propsBlock = em[4] || ""
    const dst = em[5]

    const labels = labelsRaw
      .split(",")
      .map((l) => l.trim())
      .filter(Boolean)

    const props = extractProps(propsBlock)

    if (!nodes.has(src)) {
      nodes.set(src, { id: src, group: src, labels: [], props: [] })
    }
    if (!nodes.has(dst)) {
      nodes.set(dst, { id: dst, group: dst, labels: [], props: [] })
    }

    links.push({
      source: src,
      target: dst,
      type: edgeType,
      labels,
      props,
    })
  }

  return { nodes: [...nodes.values()], links }
}

const getNodeColorInternal = (() => {
  const base = [
    "#ef4444",
    "#3b82f6",
    "#8b5cf6",
    "#ec4899",
    "#10b981",
    "#f59e0b",
    "#14b8a6",
    "#22d3ee",
    "#a3e635",
    "#f97316",
    "#6366f1",
    "#f43f5e",
    "#06b6d4",
    "#34d399",
    "#fbbf24",
    "#e879f9",
    "#60a5fa",
    "#34d399",
    "#f87171",
    "#fcd34d",
    "#a78bfa",
    "#f472b6",
    "#06b6d4",
    "#f59e0b",
    "#fb7185",
  ]
  const map = new Map()
  let i = 0
  return (name) => {
    if (!map.has(name)) map.set(name, base[i++ % base.length])
    return map.get(name)
  }
})()

export const getNodeColor = (name) => getNodeColorInternal(name)

const tooltipCardStyle = `
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
  font-size: 12.5px;
  line-height: 1.45;
  color: #111;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,.12);
  min-width: 220px;
  max-width: min(80vw, 520px);
  width: max-content;
  white-space: normal;
  overflow-wrap: anywhere;
`

const tooltipRowStyle = `
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) auto;
  gap: 8px;
  align-items: start;
  margin-top: 6px;
`

const tooltipKeyStyle = "font-weight:600; overflow-wrap:anywhere;"
const tooltipTypeStyle = "color:#4b5563; overflow-wrap:anywhere;"
const tooltipOptionalStyle =
  "justify-self:start; font-size:11px; color:#6b7280; background:#f3f4f6; border-radius:9999px; padding:1px 6px;"
const tooltipEmptyStyle = "color:#6b7280;"
const tooltipLabelsStyle = "font-size:11px; color:#4b5563; margin-bottom:4px; overflow-wrap:anywhere;"

export const drawNodeWithInnerLabel = (node, ctx, globalScale) => {
  const label = node.label || node.id
  const fontSize = Math.max(10, 12 / globalScale)
  ctx.font = `${fontSize}px Sans-Serif`

  const textW = ctx.measureText(label).width
  const r = Math.max(10, textW / 2 + 6)
  node._r = r

  ctx.beginPath()
  ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false)
  ctx.fillStyle = getNodeColor(node.id)
  ctx.fill()

  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.lineWidth = Math.max(1, 3 / globalScale)
  ctx.strokeStyle = "rgba(0,0,0,0.25)"
  ctx.strokeText(label, node.x, node.y)
  ctx.fillStyle = "#fff"
  ctx.fillText(label, node.x, node.y)
}

export const paintNodePointerArea = (node, color, ctx) => {
  const r = node._r || 14
  ctx.beginPath()
  ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false)
  ctx.fillStyle = color
  ctx.fill()
}

export const formatNodeTooltip = (n) => {
  const rows =
    (n.props || [])
      .map(
        (p) => `<div class="row" style="${tooltipRowStyle}">
      <span class="k" style="${tooltipKeyStyle}">${p.name}</span>
      <span class="t" style="${tooltipTypeStyle}">${p.type}</span>
      ${p.optional ? `<span class="o" style="${tooltipOptionalStyle}">optional</span>` : ``}
    </div>`
      )
      .join("") || `<div class="empty" style="${tooltipEmptyStyle}">-</div>`

  const labelsLine =
    (n.labels && n.labels.length > 0)
        ? `<div style="${tooltipLabelsStyle}">
            ${n.labels.length === 1 ? "Label" : "Labels"}: ${n.labels.join(", ")}
        </div>`
        : ""


  return `
  <div style="
    ${tooltipCardStyle}">
    <div style="font-weight:600; margin-bottom:4px; overflow-wrap:anywhere;">${n.id}</div>
    ${labelsLine}
    ${rows}
  </div>`
}

export const formatLinkTooltip = (l) => {
  const rows =
    (l.props || [])
      .map(
        (p) => `<div class="row" style="${tooltipRowStyle}">
      <span class="k" style="${tooltipKeyStyle}">${p.name}</span>
      <span class="t" style="${tooltipTypeStyle}">${p.type}</span>
      ${p.optional ? `<span class="o" style="${tooltipOptionalStyle}">optional</span>` : ``}
    </div>`
      )
      .join("") || `<div class="empty" style="${tooltipEmptyStyle}">-</div>`

  const labelsLine =
    (l.labels && l.labels.length > 0)
        ? `<div style="${tooltipLabelsStyle}">
            ${l.labels.length === 1 ? "Label" : "Labels"}: ${l.labels.join(", ")}
        </div>`
        : ""


  return `
  <div style="
    ${tooltipCardStyle}">
    <div style="font-weight:600; margin-bottom:4px; overflow-wrap:anywhere;">${l.type || "Edge"}</div>
    ${labelsLine}
    ${rows}
  </div>`
}
